import type { ApplicationFiltersSession, SessionMetaKey } from '../types/utils'
import { useState, useEffect, useCallback, useRef } from 'react'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { showError } from '@/lib/utils/notifications'
import { getApplicationList } from '../services/applicationService'
import type { Application, ApplicationFilters, ApplicationListResponse } from '../types/application'
import { isUnderwriter } from '../utils/applicationPolicies'
import { getRoleScopedFilterLock } from '../utils/applicationListLogic'
import {
  stripApplicationFiltersMeta,
} from '../utils/applicationFormatters'

/** Default filters for init/reset (Angular defaultFilter; underwriter default only at init). */
const BASE_DEFAULT_FILTERS: ApplicationFilters = {
  page: 1,
  perPage: 15,
  sortBy: 'desc',
  orderBy: 'applicant_id',
}

/** Application list data and filters (session storage; Angular parity). */
export function useApplicationList() {
  // using session storage instead
  const user = useAppSelector(selectUser)
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(false)
  const [sorting, setSorting] = useState(false) // Tracks in-flight sort; list loader uses `loading`
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  // Track current filters internally (for sort state that doesn't update URL)
  const [currentFilters, setCurrentFilters] = useState<ApplicationFilters | null>(null)
  const currentFiltersRef = useRef<ApplicationFilters | null>(null)

  // Keep ref in sync with state
  const updateCurrentFilters = useCallback((filters: ApplicationFilters | null) => {
    currentFiltersRef.current = filters
    setCurrentFilters(filters)
  }, [])

  // Default filters based on user role
  const getDefaultFilters = useCallback((): ApplicationFilters => {
    // Company-/property-scoped roles get their fixed ids patched in (Angular getAccessiblePermission)
    const roleLock = getRoleScopedFilterLock(user)
    // Check if user is ROLE_UNDERWRITER - default to PENDING status and underwriting_sort
    if (isUnderwriter(user)) {
      return {
        page: 1,
        perPage: 15,
        sortBy: 'asc',
        orderBy: 'underwriting_sort',
        applicantStatus: ['PENDING'],
        ...roleLock,
      }
    }
    return { ...BASE_DEFAULT_FILTERS, ...roleLock }
  }, [user])

  // Load filters from session storage
  const loadFiltersFromSessionStorage = useCallback((): ApplicationFilters => {
    try {
      const saved = sessionStorage.getItem('applicationlist_filter')
      if (saved) {
        return JSON.parse(saved) as ApplicationFilters
      }
    } catch (e) {
      console.error('Failed to load filters from session:', e)
    }
    return getDefaultFilters()
  }, [getDefaultFilters])

  // Save filters to session storage
  const saveFiltersToSessionStorage = useCallback(
    (filters: ApplicationFilters, meta?: Partial<Record<SessionMetaKey, string>>) => {
      try {
        const payload: ApplicationFiltersSession = {
          ...(filters as ApplicationFiltersSession),
          ...(meta || {}),
        }
        sessionStorage.setItem('applicationlist_filter', JSON.stringify(payload))
      } catch (e) {
        console.error('Failed to save filters to session:', e)
      }
    },
    []
  )

  // Clear filters from session storage
  const clearFiltersFromSessionStorage = useCallback(() => {
    try {
      sessionStorage.removeItem('applicationlist_filter')
    } catch (e) {
      console.error('Failed to clear filters from session:', e)
    }
  }, [])

  // Initialize filters from Session Storage (primary) or Defaults
  const getInitialFilters = useCallback((): ApplicationFilters => {
    // 1. Try to load from session storage (Primary source for "old app" behavior)
    try {
      const sessionFilters = loadFiltersFromSessionStorage()
      // If we have valid session filters, use them
      if (sessionFilters && Object.keys(sessionFilters).length > 0) {
        // Merge with defaults to ensure all required fields exist
        return { ...getDefaultFilters(), ...sessionFilters }
      }
    } catch (e) {
      console.error('Error loading filters', e)
    }

    // 2. Fallback to defaults
    return getDefaultFilters()
  }, [loadFiltersFromSessionStorage, getDefaultFilters])

  // Fetch applications (with loading indicator)
  const fetchApplications = useCallback(async (filters: ApplicationFilters, showLoading = true) => {
    if (showLoading) {
      setLoading(true)
    }
    setError(null)

    try {
      const response: ApplicationListResponse = await getApplicationList(
        stripApplicationFiltersMeta(filters)
      )
      setApplications(response.data || [])
      setPagination({
        current: response.current || 1,
        last: response.last || 1,
        total: response.total || 0,
      })
    } catch (err) {
      const errorMessage =
        err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'Failed to fetch applications'
      // Access Denied for impersonated renter — toast only, not inline error panel.
      if (errorMessage === 'Access Denied' && user?.current_role?.role_name === 'ROLE_RENTER') {
        showError(errorMessage)
      } else {
        setError(errorMessage)
      }
      setApplications([])
    } finally {
      if (showLoading) {
        setLoading(false)
      }
      setSorting(false)
    }
  }, [user])

  // Apply filters
  const applyFilters = useCallback(
    (newFilters: Partial<ApplicationFilters>, meta?: Partial<Record<SessionMetaKey, string>>) => {
      const baseFilters = currentFiltersRef.current || getInitialFilters()

      // Check if only page is being changed (no other filters)
      const isOnlyPageChange = Object.keys(newFilters).length === 1 && 'page' in newFilters

      // Page: explicit value, page-only change, or reset to 1 when other filters change.
      const pageValue =
        newFilters.page !== undefined
          ? newFilters.page
          : isOnlyPageChange
            ? baseFilters.page
            : 1

      const updatedFilters: ApplicationFilters = {
        ...baseFilters,
        ...newFilters,
        ...(meta || {}),
        page: pageValue,
      }

      // Update internal state
      updateCurrentFilters(updatedFilters)

      // Save to session
      saveFiltersToSessionStorage(updatedFilters, meta)

      // Fetch data
      fetchApplications(updatedFilters)
    },
    [getInitialFilters, saveFiltersToSessionStorage, fetchApplications, updateCurrentFilters]
  )

  // Reset — generic defaultFilter for all roles; scoped ids re-applied (Angular resetFilter).
  const resetFilters = useCallback(() => {
    // Role-scoped company/property re-applied on reset (Angular getAccessiblePermission).
    const defaults: ApplicationFilters = { ...BASE_DEFAULT_FILTERS, ...getRoleScopedFilterLock(user) }
    clearFiltersFromSessionStorage()
    updateCurrentFilters(defaults)
    fetchApplications(defaults)
  }, [user, clearFiltersFromSessionStorage, fetchApplications, updateCurrentFilters])

  // Change page
  const changePage = useCallback(
    (page: number) => {
      applyFilters({ page })
    },
    [applyFilters]
  )

  // Change per page
  const changePerPage = useCallback(
    (perPage: number) => {
      applyFilters({ perPage, page: 1 })
    },
    [applyFilters]
  )

  // Sort
  const sort = useCallback(
    (orderBy: string) => {
      const baseFilters = currentFiltersRef.current || getInitialFilters()
      const newSortBy =
        baseFilters.orderBy === orderBy && baseFilters.sortBy === 'desc' ? 'asc' : 'desc'

      // Create updated filters with new sort params
      const updatedFilters: ApplicationFilters = {
        ...baseFilters,
        orderBy,
        sortBy: newSortBy,
      }

      // Update internal state (for UI to reflect sort changes)
      updateCurrentFilters(updatedFilters)
      setSorting(true)

      // Save to session storage
      saveFiltersToSessionStorage(updatedFilters)

      // Match other listing modules: sorting shows the list loader while data refreshes.
      fetchApplications(updatedFilters, true)
    },
    [getInitialFilters, saveFiltersToSessionStorage, fetchApplications, updateCurrentFilters]
  )

  // Initial load
  useEffect(() => {
    const filters = getInitialFilters()
    updateCurrentFilters(filters)
    fetchApplications(filters)
  }, []) // Only run on mount

  // Get the effective filters (internal state takes precedence)
  const getEffectiveFilters = useCallback((): ApplicationFilters => {
    return currentFilters || getInitialFilters()
  }, [currentFilters, getInitialFilters])

  return {
    applications,
    loading,
    sorting,
    error,
    pagination,
    filters: getEffectiveFilters(),
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch: () => {
      const filters = getEffectiveFilters()
      fetchApplications(filters)
    },
  }
}

